import React, { useState, useEffect } from 'react';
import { Box, Typography, Select, Option } from '@mui/joy';

// Simple, theme-proof progress bar (see JobStatusChart for rationale).
const ProgressBar: React.FC<{ value: number }> = ({ value }) => (
  <Box sx={{ height: 8, borderRadius: '4px', backgroundColor: 'background.level2', overflow: 'hidden' }}>
    <Box
      sx={{
        height: '100%',
        width: `${Math.min(Math.max(value, 0), 100)}%`,
        backgroundColor: 'primary.500',
        borderRadius: '4px',
        transition: 'width 0.4s ease',
      }}
    />
  </Box>
);
import type { StatusBreakdownItem, BoardInfo } from '../../hooks/useDashboard';

interface SalesPipelineChartProps {
  boards: BoardInfo[];
  data: Record<string, StatusBreakdownItem[]>;
}

export const SalesPipelineChart: React.FC<SalesPipelineChartProps> = ({ boards, data }) => {
  const [selectedBoard, setSelectedBoard] = useState<string>('all');

  useEffect(() => {
    if (boards.length === 1) {
      setSelectedBoard(String(boards[0].id));
    }
  }, [boards]);

  if (!boards || boards.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography level="body-md" sx={{ color: 'text.secondary' }}>
          No opportunity boards found.
        </Typography>
      </Box>
    );
  }

  const renderStatusBars = (items: StatusBreakdownItem[]) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {items.map((status, index) => (
        <Box key={status.board_status__name || index}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography level="body-md">{status.board_status__name}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                {status.count}
              </Typography>
              <Box
                sx={{
                  backgroundColor: 'primary.500',
                  color: 'white',
                  px: 1,
                  py: 0.25,
                  borderRadius: 'sm',
                  fontSize: '0.75rem',
                  minWidth: '2rem',
                  textAlign: 'center',
                }}
              >
                {status.percentage}%
              </Box>
            </Box>
          </Box>
          <ProgressBar value={status.percentage} />
        </Box>
      ))}
    </Box>
  );

  const renderAllBoardsSummary = () => {
    const boardSummaries = boards.map((board) => {
      const statuses = data[String(board.id)] || [];
      const total = statuses.reduce((sum, s) => sum + s.count, 0);
      return { name: board.name, total };
    });
    const grandTotal = boardSummaries.reduce((sum, b) => sum + b.total, 0);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {boardSummaries.map((board) => {
          const pct = grandTotal > 0 ? Math.round((board.total / grandTotal) * 100) : 0;
          return (
            <Box key={board.name}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography level="body-md">{board.name}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    {board.total} opportunities
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: 'primary.500',
                      color: 'white',
                      px: 1,
                      py: 0.25,
                      borderRadius: 'sm',
                      fontSize: '0.75rem',
                      minWidth: '2rem',
                      textAlign: 'center',
                    }}
                  >
                    {pct}%
                  </Box>
                </Box>
              </Box>
              <ProgressBar value={pct} />
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box>
      {boards.length > 1 && (
        <Select
          value={selectedBoard}
          onChange={(_, val) => val && setSelectedBoard(val)}
          size="sm"
          sx={{ mb: 2, maxWidth: 250 }}
        >
          <Option value="all">All Boards</Option>
          {boards.map((board) => (
            <Option key={board.id} value={String(board.id)}>
              {board.name}
            </Option>
          ))}
        </Select>
      )}
      {selectedBoard === 'all'
        ? renderAllBoardsSummary()
        : renderStatusBars(data[selectedBoard] || [])}
    </Box>
  );
};
